if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // uShopAid bundles: the app's picker never mounts inside this popup, so build our own
            return BhUshBundleQuickAdd.mount(this.modalContent, responseHTML).then(() => super.show(opener));
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        const nestedQuickAddModals = productElement.querySelectorAll('quick-add-modal');
        if (nestedQuickAddModals) nestedQuickAddModals.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

/*
  Quick-add support for bundles built with the uShopAid Bundle Builder app.

  The app renders its per-item Size/Color picker on the PDP with its own script, which never
  runs inside this popup — so the popup only showed the bundle's single "Default Title" variant.
  This rebuilds the picker from the app's config (the ush_bundle_product_config JSON that ships
  with the product page) and writes the same line item properties the app writes on the PDP:
    _ush_bundle_unit   = "<activityId>:<timestamp>"
    <item title>       = "<option value> / <option value>"
    _bundle_selection  = "<productId>^<variantId>:<priceCents>:<qty>,..."
  The app's cart transform reads those to expand and price the bundle; without _bundle_selection
  Shopify rejects the add ("The bundle product ... cannot be added to the cart").
  Only the app's auto-synced pricing is mirrored (every Bundle on the store uses it). Anything
  else falls back to a "View Bundle" link to the product page rather than risk a failed add.
*/
var BhUshBundleQuickAdd = (() => {
  const productCache = new Map();

  // Styles ship with the script so the picker never renders unstyled (e.g. a cached or
  // not-yet-uploaded quick-add.css)
  const STYLES = `.bh-ush-qa { order: 2; width: 100%; border: 1px solid #d9d9d9; border-radius: 4px; padding: 16px; margin: 0; } .bh-ush-qa__item + .bh-ush-qa__item { border-top: 1px solid #d9d9d9; margin-top: 16px; padding-top: 16px; } .bh-ush-qa__head { display: flex; align-items: flex-start; gap: 16px; } .bh-ush-qa__img { flex: 0 0 80px; width: 80px; height: 80px; border: 1px solid #d9d9d9; } .bh-ush-qa__img img { width: 100%; height: 100%; object-fit: contain; display: block; } .bh-ush-qa__info { flex: 1 1 auto; min-width: 0; } .bh-ush-qa__title, .bh-ush-qa__price { margin: 0 0 6px; font-size: 15px; line-height: 1.3; color: #000; } .bh-ush-qa__title { font-weight: 700; } .bh-ush-qa__details { flex: 0 0 auto; font-size: 12px; color: #424242; text-decoration: underline; } .bh-ush-qa__option { margin-top: 12px; } .bh-ush-qa__option-name { margin: 0 0 6px; font-size: 12px; font-weight: 700; color: #000; } .bh-ush-qa__option-name span { font-weight: 400; opacity: 0.65; } .bh-ush-qa__values { display: flex; flex-wrap: wrap; gap: 8px; } .bh-ush-qa__value { min-height: 32px; padding: 4px 10px; border: 1px solid #d9d9d9; background: #fff; color: #000; font-size: 13px; line-height: 1.2; cursor: pointer; } .bh-ush-qa__value.is-selected { background: #03314c; border-color: #03314c; color: #fff; } .bh-ush-qa__value--swatch { width: 36px; height: 36px; padding: 2px; } .bh-ush-qa__value--swatch.is-selected { background: #fff; border-color: #000; } .bh-ush-qa__swatch-fill { display: block; width: 100%; height: 100%; border: 1px solid rgba(0, 0, 0, 0.08); } .bh-ush-qa__value.is-unavailable { opacity: 0.4; text-decoration: line-through; }`;
  const injectStyles = () => {
    if (document.getElementById('bh-ush-qa-styles')) return;
    const style = document.createElement('style');
    style.id = 'bh-ush-qa-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
  };

  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function formatMoney(cents) {
    const format = (window.Shopify && Shopify.money_format) || window.__UShopAidLiquidData?.money_format || '${{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const withDelimiters = (precision, thousands, decimal) => {
      const parts = (cents / 100).toFixed(precision).split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
    };
    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = withDelimiters(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(0, '.', ''); break;
      default: value = withDelimiters(2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  // Component products are only known by id, so resolve the handle via /variants/:id (Shopify
  // redirects it to the product page) and then load the product's Ajax JSON.
  function fetchProduct(productId, variantId) {
    if (productCache.has(productId)) return productCache.get(productId);
    const request = fetch(`${window.Shopify?.routes?.root || '/'}variants/${variantId}`, { method: 'HEAD' })
      .then((response) => fetch(new URL(response.url).pathname.replace(/\/$/, '') + '.js'))
      .then((response) => {
        if (!response.ok) throw new Error(`Bundle item ${productId} request failed`);
        return response.json();
      });
    request.catch(() => productCache.delete(productId));
    productCache.set(productId, request);
    return request;
  }

  function readConfig(doc) {
    const script = doc.querySelector('script[data-name="ush_bundle_product_config"]');
    if (!script) return null;
    try {
      let config = JSON.parse(script.textContent.trim() || 'null');
      if (typeof config === 'string') config = JSON.parse(config);
      return config && config.options ? config : null;
    } catch (e) {
      return null;
    }
  }

  // Swap Add to Cart for a link to the bundle's product page, where the app's own picker works
  function linkToProduct(form, responseHTML) {
    const submit = form.querySelector('[type="submit"][name="add"]') || form.querySelector('.product-form__submit');
    const url = responseHTML.querySelector('link[rel="canonical"]')?.getAttribute('href');
    if (!submit || !url) return;
    const link = document.createElement('a');
    link.href = url;
    link.className = submit.className;
    link.textContent = window.__UShopAid?.quickAddRule?.viewBundleText?.[0]?.translate || 'View Bundle';
    submit.replaceWith(link);
    form.querySelectorAll('.shopify-payment-button').forEach((el) => el.remove());
  }

  function mount(container, responseHTML) {
    const config = readConfig(responseHTML);
    if (!config) return Promise.resolve();

    const form = container.querySelector('product-form form[action*="/cart/add"]');
    const idInput = form && form.querySelector('[name="id"]');
    if (!form || !idInput) return Promise.resolve();

    const bundleVariantId = config.options[idInput.value] ? idInput.value : Object.keys(config.options)[0];
    const groups = config.options[bundleVariantId] || [];
    const values = (config.values && config.values[bundleVariantId]) || {};
    const policy = (config.policy && config.policy[bundleVariantId]) || {};
    if (!groups.length) return Promise.resolve();

    const pricePolicy = policy.pricePolicy || {};
    const supported =
      pricePolicy.autoSync &&
      ['PERCENTAGE', 'NONE'].includes(pricePolicy.discountType) &&
      !pricePolicy.discountScope &&
      !(policy.freeGifts || []).length;
    if (!supported) return Promise.resolve(linkToProduct(form, responseHTML));

    // Map each item's component variants to the option-value indices that select them.
    // Keys look like "productId:variantId"; each entry's options are "group:option:value".
    const variantsByGroup = groups.map(() => []);
    Object.entries(values).forEach(([key, entries]) => {
      const [productId, variantId] = key.split(':');
      (entries || []).forEach((entry) => {
        const triples = (entry.options || []).map((t) => t.split(':').map(Number));
        const groupIndex = triples.length
          ? triples[0][0]
          : groups.findIndex((group) => String(group.product?.productId) === productId);
        if (!variantsByGroup[groupIndex]) return;
        const picks = {};
        triples.forEach(([, optionIndex, valueIndex]) => (picks[optionIndex] = valueIndex));
        variantsByGroup[groupIndex].push({ productId, variantId, qty: entry.qty || 1, picks });
      });
    });

    // Swatch colors: the theme's swatch_color_list first (same as the rest of the site), then the app's own
    let themeSwatches = {};
    try {
      themeSwatches = JSON.parse(responseHTML.querySelector('script[data-bh-swatch-map]')?.textContent || '{}');
    } catch (e) {}
    const swatchKey = (value) => String(value).toLowerCase().replace(/\s/g, '');
    const swatchMap = {};
    (config.styleCustom?.swatchSetting?.swatches || []).forEach((swatch) => {
      swatchMap[swatch.name] = {};
      (swatch.values || []).forEach((v) => (swatchMap[swatch.name][v.optionValue] = v.swatchValue));
    });

    const loads = groups.map((group, groupIndex) => {
      const first = variantsByGroup[groupIndex][0];
      const productId = group.product?.productId || first?.productId;
      if (!productId || !first) return Promise.resolve(null);
      return fetchProduct(String(productId), first.variantId).catch(() => null);
    });

    return Promise.all(loads).then((products) => {
      if (products.some((p) => !p)) return linkToProduct(form, responseHTML);

      const state = groups.map((group) =>
        (group.options || []).map((option) => Math.max(0, (option.values || []).indexOf(option.defaultValue)))
      );

      const findComponent = (groupIndex, picks) =>
        variantsByGroup[groupIndex].find((c) => Object.keys(c.picks).every((o) => c.picks[o] === picks[o]));

      const componentVariant = (groupIndex, picks) => {
        const component = findComponent(groupIndex, picks);
        if (!component) return null;
        const variant = products[groupIndex].variants.find((v) => String(v.id) === String(component.variantId));
        return variant ? { component, variant } : null;
      };

      injectStyles();
      const picker = document.createElement('div');
      picker.className = 'bh-ush-qa';
      picker.innerHTML = groups
        .map((group, g) => {
          const product = products[g];
          const image = product.featured_image ? product.featured_image + (product.featured_image.includes('?') ? '&' : '?') + 'width=200' : '';
          const options = (group.options || [])
            .map((option, o) => {
              const appSwatches = swatchMap[`${group.title} - ${option.title}`];
              const isColor = !!appSwatches || /colou?r/i.test(option.title);
              const swatchFor = (value) => themeSwatches[swatchKey(value)] || (appSwatches && appSwatches[value]);
              const swatches = isColor && (option.values || []).every((value) => swatchFor(value)) ? true : null;
              const buttons = (option.values || [])
                .map((value, v) => {
                  const swatch = swatches && swatchFor(value);
                  const body = swatch
                    ? `<span class="bh-ush-qa__swatch-fill" style="background:${esc(swatch)}"></span><span class="visually-hidden">${esc(value)}</span>`
                    : esc(value);
                  return `<button type="button" class="bh-ush-qa__value${swatch ? ' bh-ush-qa__value--swatch' : ''}" data-g="${g}" data-o="${o}" data-v="${v}" title="${esc(value)}" aria-pressed="false">${body}</button>`;
                })
                .join('');
              return `<div class="bh-ush-qa__option">
                  <p class="bh-ush-qa__option-name">${esc(option.title)}${swatches ? `: <span data-selected="${g}-${o}"></span>` : ''}</p>
                  <div class="bh-ush-qa__values">${buttons}</div>
                </div>`;
            })
            .join('');
          return `<div class="bh-ush-qa__item">
              <div class="bh-ush-qa__head">
                <a class="bh-ush-qa__img" href="${esc(product.url)}" tabindex="-1" aria-hidden="true">${image ? `<img src="${esc(image)}" alt="" loading="lazy" width="100" height="100" data-img="${g}">` : ''}</a>
                <div class="bh-ush-qa__info">
                  <p class="bh-ush-qa__title">${esc(group.title || product.title)}</p>
                  <p class="bh-ush-qa__price" data-price="${g}"></p>
                </div>
                <a class="bh-ush-qa__details" href="${esc(product.url)}" target="_blank" rel="noopener">View Details</a>
              </div>
              ${options}
            </div>`;
        })
        .join('');

      const buyButtons = container.querySelector('[id^="BhBuyButtons-"]') || form.closest('.product-form');
      buyButtons.parentNode.insertBefore(picker, buyButtons);

      const setProperty = (name, value) => {
        let input = form.querySelector(`input.bh-ush-qa-input[data-name="${CSS.escape(name)}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.className = 'bh-ush-qa-input';
          input.dataset.name = name;
          input.name = `properties[${name}]`;
          form.appendChild(input);
        }
        input.value = value;
      };
      const stampUnit = () => setProperty('_ush_bundle_unit', `${config.activityId}:${Date.now().toString(36).toUpperCase()}`);

      // Mirrors the app's auto-synced price math: each item's price less the bundle discount, summed
      const discountPct = pricePolicy.discountType === 'PERCENTAGE' ? Number(pricePolicy.discountValue || 0) : 0;

      const render = () => {
        let total = 0;
        let compare = 0;
        let available = true;
        const seenTitles = [];
        const selection = [];

        groups.forEach((group, g) => {
          const picks = state[g];
          const match = componentVariant(g, picks);
          (group.options || []).forEach((option, o) => {
            picker.querySelectorAll(`[data-g="${g}"][data-o="${o}"]`).forEach((button) => {
              const v = Number(button.dataset.v);
              const selected = picks[o] === v;
              const trial = picks.slice();
              trial[o] = v;
              const candidate = componentVariant(g, trial);
              button.classList.toggle('is-selected', selected);
              button.setAttribute('aria-pressed', selected);
              button.classList.toggle('is-unavailable', !candidate || !candidate.variant.available);
            });
            const label = picker.querySelector(`[data-selected="${g}-${o}"]`);
            if (label) label.textContent = option.values[picks[o]] || '';
          });

          const priceEl = picker.querySelector(`[data-price="${g}"]`);
          if (!match) {
            available = false;
            priceEl.textContent = '';
            return;
          }
          const { variant, component } = match;
          if (!variant.available) available = false;
          priceEl.textContent = formatMoney(variant.price);
          // Show the chosen variant's photo (e.g. the selected color)
          const imgEl = picker.querySelector(`[data-img="${g}"]`);
          const variantImage = variant.featured_image?.src || products[g].featured_image;
          if (imgEl && variantImage) {
            const src = variantImage + (variantImage.includes('?') ? '&' : '?') + 'width=200';
            if (imgEl.getAttribute('src') !== src) imgEl.setAttribute('src', src);
          }
          total += Math.floor((variant.price * (100 - discountPct)) / 100) * component.qty;
          selection.push(
            `${config.styleNo > 1 ? `${component.productId}^` : ''}${component.variantId}:${variant.price}:${component.qty}`
          );
          compare += Math.max(variant.compare_at_price || variant.price, variant.price) * component.qty;

          // Same property naming as the app: item title, suffixed when a title repeats
          const repeats = seenTitles.filter((t) => t === group.title).length;
          seenTitles.push(group.title);
          setProperty(
            group.title + (repeats ? ` ${repeats}` : ''),
            (group.options || []).map((option, o) => option.values[picks[o]]).join(' / ')
          );
        });
        stampUnit();
        setProperty('_bundle_selection', available ? selection.join(',') : '');
        // Full-price total, so the cart drawer can show the bundle's real savings
        setProperty('_bh_compare', compare > total ? compare : '');

        const priceText = formatMoney(total);
        const priceBlock = container.querySelector('[id^="bh-price-"]');
        if (priceBlock) {
          const priceEl = priceBlock.querySelector('[data-bh-price]');
          const compareEl = priceBlock.querySelector('[data-bh-compare]');
          const saveEl = priceBlock.querySelector('[data-bh-save]');
          const saveAmountEl = priceBlock.querySelector('[data-bh-save-pct]');
          if (priceEl) priceEl.textContent = priceText;
          const hasSaving = compare > total;
          if (compareEl) {
            compareEl.textContent = formatMoney(compare);
            compareEl.style.display = hasSaving ? '' : 'none';
          }
          if (saveEl) saveEl.style.display = hasSaving ? '' : 'none';
          if (saveAmountEl && hasSaving) {
            saveAmountEl.textContent = saveAmountEl.textContent.includes('%')
              ? `${Math.round(((compare - total) / compare) * 100)}%`
              : formatMoney(compare - total);
          }
        }

        const submit = form.querySelector('[type="submit"][name="add"]') || form.querySelector('.product-form__submit');
        const label = form.querySelector('[data-bh-add-to-cart-label]') || submit?.querySelector('span');
        if (!submit) return;
        if (submit.dataset.bhUshOriginal === undefined) submit.dataset.bhUshOriginal = (label?.textContent || '').trim();
        const original = submit.dataset.bhUshOriginal;
        if (available) {
          submit.removeAttribute('disabled');
          // "Add to Cart - $X": swap in the bundle total; labels without a price stay as they were
          if (label) label.textContent = /\s-\s/.test(original) ? `${original.split(/\s+-\s+/)[0]} - ${priceText}` : original;
        } else {
          submit.setAttribute('disabled', 'disabled');
          if (label) label.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      };

      picker.addEventListener('click', (event) => {
        const button = event.target.closest('.bh-ush-qa__value');
        if (!button) return;
        state[button.dataset.g][button.dataset.o] = Number(button.dataset.v);
        render();
      });
      // Fresh bundle unit id for every add, so repeat adds stay separate bundles like on the PDP
      form.addEventListener('submit', stampUnit, true);

      render();
    });
  }

  return { mount };
})();
